import { useGlobalProperty } from '@/hooks/useGlobalProperty';
import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { propertyAPI } from '@/services/api';
import { Users, Plus, Trash2, Building, User, CreditCard, Phone, Mail, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CarteraClientesPage() {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useGlobalProperty(properties);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({
    tipo: 'natural',
    nombre: '',
    documento: '',
    telefono: '',
    email: '',
    empresa_nombre: '',
    credito_limite: '',
    plazo_dias_default: '30'
  });

  useEffect(() => { fetchProperties(); }, []);
  useEffect(() => { if (selectedProperty) fetchClients(); }, [selectedProperty]);

  const fetchProperties = async () => {
    try {
      const res = await propertyAPI.getAll();
      setProperties(res.data);
      
    } catch (e) { toast.error('Error al cargar propiedades'); }
    finally { setLoading(false); }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BACKEND_URL}/api/preferred-clients?property_id=${selectedProperty}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(res.data);
    } catch (e) { toast.error('Error al cargar clientes'); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/preferred-clients`, {
        property_id: selectedProperty,
        tipo: form.tipo,
        nombre: form.nombre,
        documento: form.documento,
        telefono: form.telefono,
        email: form.email,
        empresa_nombre: form.tipo === 'empresa' ? form.empresa_nombre : '',
        credito_limite: parseFloat(form.credito_limite) || 0,
        plazo_dias_default: parseInt(form.plazo_dias_default)
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Cliente preferencial agregado');
      setDialogOpen(false);
      setForm({ tipo: 'natural', nombre: '', documento: '', telefono: '', email: '', empresa_nombre: '', credito_limite: '', plazo_dias_default: '30' });
      fetchClients();
    } catch (e) { toast.error('Error al crear cliente'); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/preferred-clients/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Cliente "${deleteTarget.nombre}" eliminado de la cartera`);
      setDeleteTarget(null);
      fetchClients();
    } catch (e) { toast.error('Error al eliminar'); }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="cartera-clientes-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope'}}>Cartera de Clientes Preferenciales</h1>
            <p className="text-muted-foreground mt-1">Clientes autorizados para crédito</p>
          </div>
          <div className="flex items-center gap-3">
            {properties.length > 0 && (
              <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                <SelectTrigger className="w-[220px]" data-testid="cartera-property-selector">
                  <SelectValue placeholder="Propiedad" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-client-btn"><Plus className="h-4 w-4 mr-2" />Agregar Cliente</Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Nuevo Cliente Preferencial</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Tipo de Cliente</Label>
                    <Select value={form.tipo} onValueChange={(v) => setForm({...form, tipo: v})}>
                      <SelectTrigger data-testid="client-tipo-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="natural">Persona Natural</SelectItem>
                        <SelectItem value="empresa">Empresa</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.tipo === 'empresa' && (
                    <div className="space-y-2">
                      <Label>Nombre de la Empresa</Label>
                      <Input placeholder="Empresa S.A.S." value={form.empresa_nombre} onChange={(e) => setForm({...form, empresa_nombre: e.target.value})} required data-testid="client-empresa-input" />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>{form.tipo === 'empresa' ? 'Contacto / Representante' : 'Nombre Completo'}</Label>
                    <Input placeholder="Nombre completo" value={form.nombre} onChange={(e) => setForm({...form, nombre: e.target.value})} required data-testid="client-nombre-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>{form.tipo === 'empresa' ? 'NIT' : 'Cédula'}</Label>
                      <Input placeholder="123456789" value={form.documento} onChange={(e) => setForm({...form, documento: e.target.value})} data-testid="client-documento-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Teléfono</Label>
                      <Input placeholder="+57 300..." value={form.telefono} onChange={(e) => setForm({...form, telefono: e.target.value})} data-testid="client-telefono-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input type="email" placeholder="correo@ejemplo.com" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="client-email-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Límite de Crédito ($)</Label>
                      <Input type="number" min="0" placeholder="5000000" value={form.credito_limite} onChange={(e) => setForm({...form, credito_limite: e.target.value})} data-testid="client-limite-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Plazo (días)</Label>
                      <Select value={form.plazo_dias_default} onValueChange={(v) => setForm({...form, plazo_dias_default: v})}>
                        <SelectTrigger data-testid="client-plazo-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10 días</SelectItem>
                          <SelectItem value="15">15 días</SelectItem>
                          <SelectItem value="30">30 días</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button type="submit" className="w-full" data-testid="client-submit-btn">Guardar Cliente</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><Users className="h-5 w-5 text-blue-600" /></div>
              <div><p className="text-2xl font-bold">{clients.length}</p><p className="text-xs text-muted-foreground">Total Clientes</p></div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center"><User className="h-5 w-5 text-green-600" /></div>
              <div><p className="text-2xl font-bold">{clients.filter(c => c.tipo === 'natural').length}</p><p className="text-xs text-muted-foreground">Personas Naturales</p></div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center"><Building className="h-5 w-5 text-purple-600" /></div>
              <div><p className="text-2xl font-bold">{clients.filter(c => c.tipo === 'empresa').length}</p><p className="text-xs text-muted-foreground">Empresas</p></div>
            </CardContent>
          </Card>
        </div>

        {/* Client List */}
        <Card>
          <CardHeader>
            <CardTitle style={{fontFamily: 'Manrope'}}>Clientes Registrados</CardTitle>
          </CardHeader>
          <CardContent>
            {clients.length === 0 ? (
              <div className="text-center py-12">
                <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No hay clientes en la cartera</p>
                <p className="text-sm text-muted-foreground mt-1">Agrega clientes para habilitarles crédito al momento del check-out</p>
              </div>
            ) : (
              <div className="space-y-3">
                {clients.map((client) => (
                  <div key={client.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/30 transition-colors" data-testid={`client-row-${client.id}`}>
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${client.tipo === 'empresa' ? 'bg-purple-100' : 'bg-blue-100'}`}>
                        {client.tipo === 'empresa' ? <Building className="h-5 w-5 text-purple-600" /> : <User className="h-5 w-5 text-blue-600" />}
                      </div>
                      <div>
                        <p className="font-medium">{client.nombre}</p>
                        {client.empresa_nombre && <p className="text-sm text-purple-600 font-medium">{client.empresa_nombre}</p>}
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                          {client.documento && <span className="flex items-center gap-1"><FileText className="h-3 w-3" />{client.documento}</span>}
                          {client.telefono && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{client.telefono}</span>}
                          {client.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{client.email}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <Badge variant="outline">{client.plazo_dias_default} días</Badge>
                        {client.saldo_credito > 0 && (
                          <p className="text-xs text-red-500 mt-1">Saldo: ${client.saldo_credito?.toLocaleString()}</p>
                        )}
                      </div>
                      <Badge className={client.tipo === 'empresa' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
                        {client.tipo === 'empresa' ? 'Empresa' : 'Natural'}
                      </Badge>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteTarget(client)} data-testid={`delete-client-${client.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Delete Dialog */}
        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar cliente?</AlertDialogTitle>
              <AlertDialogDescription>
                Se eliminará a "{deleteTarget?.nombre}" de la cartera de clientes preferenciales. Ya no podrá recibir créditos.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleDelete} data-testid="confirm-delete-client">Eliminar</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
